import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
	Typography,
	TextInput as Input,
	Form,
	Button,
	Select,
	Alert,
	Row,
	Col,
	Transfer,
	notification,
	Icon,
} from '@/elements';
import { Checkbox, Tooltip } from 'antd';
import { List } from 'react-virtualized';
import './index.scss';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// ** Schema description:
//* name: string!,
//* label: string|ReactNode|() => ReactNode,
//* tooltip: string|ReactNode|() => ReactNode,
//* placeholder: string,
//* initialValue: depend on field,
//* type: enum:('select','transfer','checkbox-group', 'textarea', default: 'input'),
//* rules: [{https://ant.design/components/form/?locale=en-US#Validation-Rules}]
//* onChange: (value/event, setter: ({ [fieldName]: value }) => void, values: [], setFields:({
//   [fieldName]: {value: any, errors: [Error] }
// }) => void)
//* help: string|ReactNode, The prompt message. If not provided, the prompt message will be generated by the validation rule.
//* hide: (values)=> boolean, if true hide field from form,
// ** for transfer, select, checkbox-group:
//* options: [{item}],
//* optionKey: string!, select uniq key of the option from options item
//* optionValue: string/number!, select key value of the option from options item
//* optionLabel: string!, select key label of the option from options item
// ** checkbox-group:
//* emptyMsg: string, message when options is empty
// ** input, textarea:
// inputProps: {}, inner input props e.g. maxLength: 100 etc.
// ** transfer:
//* rowKey: string!, required uniq key from options object.
//* renderOption: (record)=> string/ReactNode.

//additional(help) component
const rowRenderer = ({
	key, // Unique key within array of rows
	index, // Index of row within collection
	isScrolling, // The List is currently being scrolled
	isVisible, // This row is visible within the List (eg it is not an overscanned row)
	style, // Style object to be applied to row (to position it)
	onItemSelect,
	selectedKeys,
	filteredItems,
}) => {
	return (
		<div key={key} style={style}>
			<div className={'transferRow'}>
				<Checkbox
					checked={selectedKeys.includes(filteredItems[index].key)}
					onChange={e => {
						onItemSelect(
							filteredItems[index].key,
							e.target.checked,
						);
					}}
				/>
				<Paragraph
					className={'transformItem'}
					ellipsis={{
						rows: 1,
					}}
				>
					{`${filteredItems[index].name} ${
						filteredItems[index].metadata &&
						filteredItems[index].metadata['Member Number']
							? ' - ' +
							  filteredItems[index].metadata['Member Number']
							: ''
					}`}
				</Paragraph>
			</div>
		</div>
	);
};

const renderField = (type, field, values, setter, setFields) => {
	switch (type) {
		case 'textarea':
			return (
				<TextArea
					{...field.inputProps}
					onChange={value =>
						field.onChange &&
						field.onChange(value, setter, values, setFields)
					}
				/>
			);
		case 'select':
			return (
				<Select
					placeholder={field.placeholder}
					style={{ width: '100%' }}
					onChange={value =>
						field.onChange &&
						field.onChange(value, setter, values, setFields)
					}
				>
					{field.options &&
						field.options.length > 0 &&
						field.options.map(el => (
							<Option
								key={el[field.optionKey || 'value']}
								value={el[field.optionValue || 'value']}
								disabled={
									field.optionsDisabled &&
									field.optionsDisabled(el, values)
								}
							>
								{el[field.optionLabel || 'label']}
							</Option>
						))}
				</Select>
			);
		case 'checkbox-group':
			return (
				<Checkbox.Group
					style={{ width: '100%' }}
					onChange={value =>
						field.onChange &&
						field.onChange(value, setter, values, setFields)
					}
				>
					<Row>
						{field.options && field.options.length > 0 ? (
							field.options.map(el => (
								<Col span={8} key={el[field.optionKey]}>
									<Checkbox value={el[field.optionValue]}>
										{el[field.optionLabel]}
									</Checkbox>
								</Col>
							))
						) : (
							<Alert
								type="info"
								showIcon
								message={field.emptyMsg}
							/>
						)}
					</Row>
				</Checkbox.Group>
			);
		case 'transfer':
			const modifyOpt = field.options.map(el => ({
				[field.rowKey]: el[field.rowKey],
				name: el.name,
				metadata: el.metadata,
			}));
			return (
				<Transfer
					dataSource={modifyOpt}
					showSearch
					rowKey={record => record[field.rowKey]}
					titles={field.titles}
					locale={{ itemUnit: 'people', itemsUnit: 'people' }}
					filterOption={(inputVal, opt) =>
						opt.name.search(new RegExp(inputVal, 'i')) >= 0 ||
						opt[field.rowKey].search(new RegExp(inputVal, 'i')) >= 0
					}
					listStyle={{
						width: '100%',
						height: 300,
					}}
				>
					{({ filteredItems, ...transferRenderProps }) => {
						//sort items by name
						const sorted = filteredItems.sort((a, b) =>
							a.name.localeCompare(b.name),
						);
						return (
							<List
								width={480}
								height={186}
								rowCount={filteredItems.length}
								rowHeight={32}
								rowRenderer={rowProps =>
									rowRenderer({
										...transferRenderProps,
										...rowProps,
										filteredItems: sorted,
									})
								}
							/>
						);
					}}
				</Transfer>
			);
		default:
			return (
				<Input
					{...field.inputProps}
					placeholder={field.placeholder}
					onChange={value =>
						field.onChange &&
						field.onChange(value, setter, values, setFields)
					}
				/>
			);
	}
};

const DynamicForm = props => {
	//effects
	//reset some field when type is changed
	useEffect(() => {
		props.form.resetFields(props.fieldsToReset);
	}, [props.type]);
	//handlers
	function handleSubmit(e) {
		e.preventDefault();
		const {
			onSubmit,
			form: { validateFieldsAndScroll },
		} = props;
		validateFieldsAndScroll((err, values) => {
			if (!err) {
				onSubmit && onSubmit(values);
			} else {
				notification.error({
					message: 'Error',
					description: 'Please input all required fields correctly',
					placement: 'bottomLeft',
				});
			}
		});
	}
	function handleCheckAll(e, field) {
		const {
			form: { setFieldsValue },
		} = props;
		const all = field.options.map(el => el[field.optionValue]);
		setFieldsValue({ [field.name]: e.target.checked ? all : [] });
	}
	//sub component
	const renderCheckAll = field => {
		const { getFieldValue } = props.form;
		return (
			<React.Fragment>
				<span>
					{field.label}{' '}
					{field.tooltip && (
						<Tooltip
							onClick={stopFocus}
							title={field.tooltip}
							placement={'topLeft'}
						>
							<Icon type="info-circle" />
						</Tooltip>
					)}
				</span>
				<div>
					<Checkbox
						onChange={e => handleCheckAll(e, field)}
						indeterminate={
							getFieldValue(field.name) &&
							getFieldValue(field.name).length !== 0 &&
							getFieldValue(field.name).length <
								field.options.length
						}
						checked={
							getFieldValue(field.name) &&
							getFieldValue(field.name).length ===
								field.options.length
						}
					>
						check all
					</Checkbox>
				</div>
			</React.Fragment>
		);
	};
	// hidden rule
	function isHide(field) {
		const { getFieldsValue } = props.form;
		return field.hide && field.hide(getFieldsValue());
	}
	function stopFocus(e) {
		e.stopPropagation();
		e.preventDefault();
		return false;
	}

	const {
		getFieldDecorator,
		getFieldValue,
		getFieldsValue,
		setFieldsValue,
		setFields,
	} = props.form;
	return (
		<form className={'dynamicForm-wrapper'} onSubmit={handleSubmit}>
			{props.label && <Title>{props.label}</Title>}
			{props.fields &&
				props.fields.map(field => {
					return (
						!isHide(field) && (
							<Form.Item
								key={field.name}
								labelCol={{ span: 24 }}
								wrapperCol={{ span: 24 }}
								label={
									field.type === 'checkbox-group' ? (
										renderCheckAll(field)
									) : (
										<span>
											{field.label}{' '}
											{field.tooltip && (
												<Tooltip
													onClick={stopFocus}
													title={field.tooltip}
													placement={'topLeft'}
												>
													<Icon type="info-circle" />
												</Tooltip>
											)}
										</span>
									)
								}
								help={
									field.help
										? field.help(getFieldValue(field.name))
										: undefined // undefined used instead of null by specific of lib
								}
							>
								{getFieldDecorator(field.name, {
									initialValue: field.initialValue,
									rules: field.rules,
									valuePropName:
										field.type === 'transfer'
											? 'targetKeys'
											: 'value',
								})(
									renderField(
										field.type,
										field,
										getFieldsValue(),
										setFieldsValue,
										setFields,
									),
								)}
							</Form.Item>
						)
					);
				})}
			<div className="form-action-wrap">
				<Button htmlType={'submit'} type="primary">
					Save
				</Button>
				<Button type="default" onClick={() => props.form.resetFields()}>
					Cancel
				</Button>
			</div>
		</form>
	);
};

DynamicForm.propTypes = {
	form: PropTypes.object.isRequired,
	fields: PropTypes.array.isRequired, //** function that return array of type Schema
	onSubmit: PropTypes.func.isRequired, // (values) => void
	type: PropTypes.string, // type: 'evacuation'/'core', optional used only for notification form
	fieldsToReset: PropTypes.arrayOf(PropTypes.string), // array of field name for reset, optional used only for notification form
};

export default Form.create()(DynamicForm);



// WEBPACK FOOTER //
// ./src/components/dynamic-form/index.js